/**
 * CookieBanner.js — GDPR/CCPA cookie consent banner (sticky bottom).
 *
 * - Renders ONCE per visitor (until they make a choice). Stored in
 *   localStorage (`fws_cookie_consent_v1`) + posted to /api/legal/consent
 *   for a durable audit trail keyed by a per-visitor random token.
 * - 3 buttons: "Accept all" / "Essential only" / "Manage choices"
 *   + a "Do Not Sell or Share My Info" link (CCPA/CPRA).
 * - Honors GPC: if the browser sends `Sec-GPC: 1`, we treat that as a
 *   pre-set "do_not_sell" choice and skip the banner entirely.
 * - Exposes `window.__fws_consent` so analytics/ad SDKs can gate on it.
 *
 * Privacy-by-design:
 *   • No tracking happens BEFORE consent is granted. PostHog autocapture
 *     stays off until consent.choice === 'accepted_all'.
 *   • Essential cookies (auth, CSRF, site-gate) are always allowed —
 *     they're not subject to consent under GDPR Article 6(1)(b)/(c).
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'fws_cookie_consent_v1';
const VISITOR_TOKEN_KEY = 'fws_visitor_token_v1';

export function hasConsentChoice() {
  try { return !!JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null')?.choice; } catch (_e) { return false; }
}

function genVisitorToken() {
  // 32-char URL-safe random — matches backend regex /^[A-Za-z0-9_-]{8,128}$/
  if (typeof window === 'undefined' || !window.crypto?.getRandomValues) {
    return `vt-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }
  const buf = new Uint8Array(24);
  window.crypto.getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function detectGpc() {
  // Global Privacy Control — Safari, Firefox + Brave send `navigator.globalPrivacyControl: true`.
  try {
    return !!(typeof navigator !== 'undefined' && navigator.globalPrivacyControl);
  } catch (_) { return false; }
}

async function postConsent({ token, choice, gpcSignal }) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/legal/consent`;
    await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_token: token, choice, gpc_signal: !!gpcSignal,
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      }),
    });
  } catch (e) {
    // Non-blocking — the local choice is still authoritative.
    console.warn('cookie-consent: backend log failed', e);
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [gpc, setGpc] = useState(false);
  // UX (2026-07-18 prod audit) — when the user is logged in, the fixed
  // bottom tab bar (z-50) was completely hidden UNDER this banner (z-1000)
  // until consent was answered: no visible navigation on first visit.
  // Offset the banner above the bar for signed-in users.
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const hasGpc = detectGpc();
    if (stored) {
      try {
        window.__fws_consent = JSON.parse(stored);
      } catch (_) { /* ignore parse errors — fall through to show banner */ }
      if (window.__fws_consent?.choice) {
        if (hasGpc) setGpc(true);
        return;  // already chose
      }
    }
    if (hasGpc) {
      // Honor GPC silently — pre-set "do_not_sell" without showing the banner.
      const token = window.localStorage.getItem(VISITOR_TOKEN_KEY) || genVisitorToken();
      window.localStorage.setItem(VISITOR_TOKEN_KEY, token);
      const choice = { choice: 'do_not_sell', gpc: true, at: new Date().toISOString() };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
      window.__fws_consent = choice;
      postConsent({ token, choice: 'do_not_sell', gpcSignal: true });
      setGpc(true);
      return;
    }
    setVisible(true);
  }, []);

  // Consent withdrawal / re-open (CCPA/GDPR — users must be able to change
  // their choice). A "Cookie settings" / "Do Not Sell" footer link dispatches
  // `window.dispatchEvent(new Event('fws:open-consent'))` to reopen this banner.
  // (2026-06-17 audit P1 consent-withdrawal-missing.)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reopen = () => { setExpanded(true); setVisible(true); };
    window.addEventListener('fws:open-consent', reopen);
    return () => window.removeEventListener('fws:open-consent', reopen);
  }, []);

  const choose = useCallback((c) => {
    if (typeof window === 'undefined') return;
    const token = window.localStorage.getItem(VISITOR_TOKEN_KEY) || genVisitorToken();
    window.localStorage.setItem(VISITOR_TOKEN_KEY, token);
    const record = { choice: c, gpc, at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    window.__fws_consent = record;
    // 2026-08 — let consent-gated SDKs (ad pixels) react without a reload.
    try { window.dispatchEvent(new Event('fws:consent-changed')); } catch (_e) {}
    postConsent({ token, choice: c, gpcSignal: gpc });
    setVisible(false);
  }, [gpc]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent banner"
      data-testid="cookie-banner"
      className={`fixed inset-x-0 z-[1000] print:hidden ${user ? 'bottom-[calc(72px+env(safe-area-inset-bottom))]' : 'bottom-0'}`}
    >
      <div className="mx-auto max-w-5xl m-3 rounded-2xl border-2 border-fuchsia-500/40 bg-slate-950/95 backdrop-blur-md text-slate-100 p-4 md:p-5 shadow-2xl">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 text-xs md:text-sm leading-relaxed">
            <p className="font-semibold mb-1">🍪 Your privacy choices</p>
            <p className="text-slate-300">
              We use essential cookies to run the App (sign-in, security, no-bot checks)
              and — only if you accept — optional cookies for analytics and ads.
              Read our{' '}
              <a href="/privacy" className="underline text-fuchsia-300 hover:text-fuchsia-200" data-testid="cookie-banner-privacy-link">
                Privacy Policy
              </a>{' '}
              or{' '}
              <button
                type="button"
                onClick={() => choose('do_not_sell')}
                className="underline text-fuchsia-300 hover:text-fuchsia-200"
                data-testid="cookie-banner-do-not-sell-btn"
              >
                Do Not Sell or Share My Info
              </button>
              .
            </p>
            {expanded && (
              <div className="mt-2 text-[11px] text-slate-400 space-y-1 max-w-3xl">
                <p><b>Essential:</b> auth cookies (HttpOnly), CSRF token, site-gate, anti-fraud. <i>Always on — required to use the App.</i></p>
                <p><b>Analytics:</b> PostHog product analytics + Sentry crash reports. We never sell this data.</p>
                <p><b>Advertising:</b> Google AdMob/AdSense and ad-mediation partners. May receive your IP + ad ID; you can opt out via &ldquo;Do Not Sell or Share&rdquo;. On iOS we additionally request ATT before any cross-app tracking.</p>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-slate-300 hover:text-white"
              onClick={() => setExpanded((v) => !v)}
              data-testid="cookie-banner-manage-btn"
            >
              {expanded ? 'Hide details' : 'Manage choices'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs border-slate-600 text-slate-200 hover:bg-slate-800"
              onClick={() => choose('essential_only')}
              data-testid="cookie-banner-essential-btn"
            >
              Essential only
            </Button>
            <Button
              size="sm"
              className="text-xs bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              onClick={() => choose('accepted_all')}
              data-testid="cookie-banner-accept-btn"
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
